/**
 * useActiveRings — the Active Ringers monitor (owner directive 2026-10-10).
 *
 * Keeps the set of agents currently ringing the user, so the green status bar can
 * show how many are active and the panel can prove each ringer has a live ring to
 * answer. Refreshes on an SSE nudge (a new ring / a ring state change), on window
 * focus, and on a slow poll. Answering / snoozing / dismissing updates the list
 * optimistically and then re-syncs from the server (the source of truth).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ringsApi, type ActiveRing, type ActiveRingsResponse } from '../api/rings';

const POLL_MS = 20_000;

export function useActiveRings(enabled = true) {
  const [rings, setRings] = useState<ActiveRing[]>([]);
  const [activeCount, setActiveCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const data: ActiveRingsResponse = await ringsApi.list(true);
      if (!mountedRef.current) return;
      setRings(data.rings || []);
      setActiveCount(data.active_count || 0);
    } catch {
      /* offline / unauth: keep the last known list */
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
    const t = setInterval(() => void refresh(), POLL_MS);
    const onFocus = () => void refresh();
    const onNudge = () => void refresh();
    window.addEventListener('focus', onFocus);
    window.addEventListener('saik:rings-refresh', onNudge as EventListener);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('saik:rings-refresh', onNudge as EventListener);
    };
  }, [enabled, refresh]);

  const answer = useCallback(async (id: number) => {
    try {
      await ringsApi.answer(id);
    } finally {
      void refresh();
    }
  }, [refresh]);

  const snooze = useCallback(async (id: number, minutes?: number) => {
    setRings((prev) => prev.map((r) => (r.id === id ? { ...r, ring_status: 'snoozed', is_active: false } : r)));
    try {
      await ringsApi.snooze(id, minutes);
    } finally {
      void refresh();
    }
  }, [refresh]);

  const dismiss = useCallback(async (id: number) => {
    setRings((prev) => prev.filter((r) => r.id !== id));
    try {
      await ringsApi.dismiss(id);
    } finally {
      void refresh();
    }
  }, [refresh]);

  return { rings, activeCount, loading, refresh, answer, snooze, dismiss };
}

export default useActiveRings;
